<template>
    <div id="app">
        <Header></Header>
        <main class="adjuster">
            <Splitpanes class="brench">
                <Pane size="35" class="task-text">
                    <div class="title">
                        {{ title }}
                        <a-tag v-if="passed" color="green">已通过</a-tag>
                    </div>
                    <div class="task-detail" v-html="content"></div>
                    <div class="history" v-if="history.length > 0">
                        
                    </div>
                </Pane>
                <Pane>
                    <Splitpanes horizontal>
                        <Pane size="65">
                            <div class="editor" ref="editor"></div>
                        </Pane>
                        <Pane class="bottom-pane">
                            <div class="toolbar">
                                <Status class="status" :status="status"></Status>
                                <a-button @click="reset">重置</a-button>
                                <a-button @click="run">运行</a-button>
                                <a-button type="primary" @click="judge">提交</a-button>
                            </div>
                            <div class="output">
                                <pre>{{ output }}</pre>
                            </div>
                        </Pane>
                    </Splitpanes>
                </Pane>
            </Splitpanes>
        </main>
        <Footer></Footer>
    </div>
</template>

<script>
import Header from '../components/Header.vue';
import Status from '../components/Status.vue';
import Footer from '@/components/Footer';
import { parseTaskMarkdown } from '../utils';
import * as monaco from 'monaco-editor';
import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';
import { createFormData, getSSRdata } from '../../../utils/utils';
import axios from 'axios';

const sandbox = document.createElement("iframe");
sandbox.style.display = 'none';
document.body.append(sandbox);

sandbox.contentWindow.self.eval(/* JS */`
class JudgeError extends Error {
    constructor(reason) {
        super(reason);
        this.reason = reason;
    }
}
window.JudgeError = JudgeError;
class EscapeError extends Error {
    constructor() {
        super("");
    }
}
window.EscapeError = EscapeError;
async function runSandbox(task, output = []) {
    const __log__ = console.log;
    console.log = (value) => {
        output.push(value.toString());
    }
    try {
        await task(output);
        return output;
    } catch(e) {
        throw e;
    } finally {
        console.log = __log__;
    }
}
window.runSandbox = runSandbox;
`);
function checkSyntax(script) {
    try {
        sandbox.contentWindow.self.eval(/* JS */`
        () => {
            ${ script }
        }
        `);
        return null;
    } catch (e) {
        return e;
    }
}
async function run(script, input) {
    const res = checkSyntax(script);
    if (res !== null) {
        return { status: 'syntax error', message: res.toString() };
    }
    const rawInputString = encodeURIComponent(JSON.stringify(input));
    try {
        const output = await sandbox.contentWindow.self.eval(/* JS */`
        'use strict';
        runSandbox(async () => {
            var input = JSON.parse(decodeURIComponent("${ rawInputString }"));
            console.log(\`run with input = \${ JSON.parse(decodeURIComponent("${ rawInputString }")) }\`);
            console.log("============ output start ============");
            ${ script };
        })`);
        return { status: 'normal', message: output.join("\n") };
    } catch(e) {
        return { status: 'runtime error', message: e.toString() };
    }
}

async function judge(script, input, judger) {
    const res = checkSyntax(script);
    if (res !== null) {
        return { status: 'syntax error', message: res.toString() };
    }
    const innerPrefix = "__99BA5433DF5FA898C8E078B8BA55F251__";
    if (script.includes(innerPrefix)) {
        return { status: 'runtime error', message: "请勿攻击评测程序" };
    }
    const createKey = (name) => `${ innerPrefix }${ name }__${ (Math.random() * 100000).toFixed() }`;
    const escapeKey = createKey("escape");
    const outputKey = createKey("output");
    const rawInputString = encodeURIComponent(JSON.stringify(input));
    try {
        await sandbox.contentWindow.self.eval(/* JS */`
        'use strict';
        let ${ escapeKey } = true;
        const ${ outputKey } = [];
        runSandbox(async () => {
            var input = JSON.parse(decodeURIComponent("${ rawInputString }"));
            ${ script };
            (() => {
                const input = JSON.parse(decodeURIComponent("${ rawInputString }"));
                const output = ${ outputKey };
                const assert = (condition, reason) => {
                    if (!condition) throw new JudgeError("assert failed: " + (reason || ""));
                }
                ${ escapeKey } = false;
                ${ judger };
            })();
        }, ${ outputKey }).then(() => {
            if (${ escapeKey }) throw new EscapeError();
        })`);
        return { status: 'accept', message: "" };
    } catch(e) {
        if (e instanceof sandbox.contentWindow.self.JudgeError) {
            return { status: 'wrong answer', message: e.reason };
        } else if (e instanceof sandbox.contentWindow.self.EscapeError) {
            return { status: 'runtime error', message: "检测程序被跳过, 请勿在代码顶层使用return!" };
        } else {
            return { status: 'runtime error', message: e.toString() };
        }
    }
}

export default {
    data: () => ({
        tid: 0,
        title: "",
        content: "",
        initcode: "",
        inputs: [],
        judger: "",
        output: "",
        passed: false,
        status: "unjudge",
        lastSubmission: "",
    }),
    created() {
        const { tid, title, content: markdown, passed, lastSubmission } = getSSRdata("task");
        const { content, initcode, inputs, judger } = parseTaskMarkdown(markdown);
        this.tid = tid;
        this.passed = passed;
        this.title = title;
        this.content = content;
        this.initcode = initcode;
        this.inputs = inputs;
        this.judger = judger;
        this.lastSubmission = lastSubmission;

        document.title = title + ' - H5CodeLab';
    },
    mounted() {
        const editor = monaco.editor.create(this.$refs.editor, {
            language: "javascript",
            automaticLayout: true,
        });
        editor.setValue(this.lastSubmission || this.initcode);
        this.editor = editor;
    },
    methods: {
        reset() {
            this.editor.setValue(this.initcode);
            this.output = "";
        },
        async run() {
            if (this.judgering) {
                return;
            }
            this.judgering = true;
            const script = this.editor.getValue();
            const input = this.inputs[0];
            const res = await run(script, input);
            this.status = res.status;
            this.output = res.message;
            this.judgering = false;
        },
        async judge() {
            if (this.judgering) {
                return;
            }
            this.judgering = true;
            const script = this.editor.getValue();
            this.output = "";
            const res = await (async () => {
                for (let i = 0; i < this.inputs.length; i++) {
                    const input = this.inputs[i];
                    const res = await judge(script, input, this.judger);
                    this.output += `[test ${ i+1 }] `;
                    if (res.status !== 'accept') {
                        const { status, message } = res;
                        this.output += message;
                        return status;
                    } else {
                        this.output += `passed\n`;
                    }
                }
                return 'accept';
            })();
            this.status = res;
            if (this.status === 'accept') {
                this.output += `pass all test`;
                this.passed = true;
            }
            axios.post("/backend/codelab/submit.php", createFormData({
                tid: this.tid, status: res.status, script
            })),
            this.judgering = false;
        },
    },
    components: {
        Header, Footer, Splitpanes, Pane, Status,
    }
}
</script>

<style lang="less">
html, body {
    height: 100%;
    width: 100%;
    margin: 0;
}
#app {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #2c3e50;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    cursor: default;
}
</style>

<style lang="less" scoped>
.adjuster {
    margin: 0px auto;
    flex-grow: 1;
    position: relative;
    width: 100%;
}
.brench {
    height: 100%;
    position: absolute;
}
.task-text {
    padding: 5px;

    display: flex;
    flex-direction: column;
    text-align: left;
}
.title {
    font-size: 20px;
    margin-bottom: 2px;
}
.editor {
    height: 100%;
    text-align: left;
}
.splitpanes--vertical::v-deep .splitpanes__pane {
    transition: none;
    -webkit-transition: none;
}
.splitpanes--horizontal::v-deep .splitpanes__pane {
    transition: none;
    -webkit-transition: none;
}
.splitpanes::v-deep .splitpanes__splitter {
    background: rgba(0, 0, 0, 0.15);
    min-width: 2px;
}
.bottom-pane {
    display: flex;
    flex-direction: column;
}
.toolbar {
    display: flex;
    align-items: center;
    padding: 2px;
    box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.35);
    .status {
        font-size: 18px;
        font-weight: bold;
        margin-right: auto;
    }
    .ant-btn {
        margin: 0 5px;
    }
}
.output {
    text-align: left;
    overflow: auto;
}
</style>